"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";

import { OptimizedImage } from "@/components/ui/optimized-image";
import { useCurrency } from "@/contexts/CurrencyContext";
// "지원 언어" 문구는 공개 언어만 센다 (숨김 출시 언어 제외)
import { publicLocales as locales, type Locale } from "@/i18n/routing";
import { bikeLicenseText, getBikeClass } from "@/lib/mobility/bike-classes";
import { bikeTypeLabel } from "@/lib/mobility/bike-facets";
import { cn } from "@/lib/utils";
import type {
  LandingBlocks,
  LandingFaqItem,
  LandingOptionGroup,
  LandingPrice,
} from "@/lib/seo/landing-blocks";

/** 예약 화면 언어(자기 이름) — routing.locales 순서 */
const LANGUAGE_NAMES: Record<Locale, string> = {
  ko: "한국어",
  en: "English",
  ja: "日本語",
  "zh-CN": "简体中文",
  "zh-TW": "繁體中文",
};

/** 놓이는 자리별 좌우 여백 — 섹션 본문 · 카루셀(섹션 여백을 상쇄해 끝까지 스크롤) */
const GUTTERS = {
  mobile: { section: "px-4", scroller: "-mx-4 px-4" },
  hubMobile: { section: "px-3", scroller: "-mx-3 px-3" },
  padded: { section: "-mx-6 px-6", scroller: "-mx-6 px-6" },
} as const;

/** 앞 몇 장만 즉시 받는다(카루셀 첫 화면), 나머지는 지연 */
const EAGER_IMAGES = 4;

/**
 * 색인 랜딩 하단 안내 블록(SEO 4단계 탭 5) — 목록 끝, "함께 찾는 렌탈" 바로 위.
 * 데이터는 서버가 만든다(lib/seo/landing-blocks). 한 화면에 한 벌만 — 호출부가 활성 트리
 * (SSR 은 데스크톱 트리)에서만 그리고 모바일 미리보기에서는 그리지 않는다.
 *
 * 섹션 머리 뒤에 아주 옅은 주황 번짐(장식, aria-hidden). `gutter` 는 놓이는 자리의 여백 —
 * 일반 검색 모바일 목록은 px-4, 허브 모바일 드로어는 행·머리와 같은 px-3(HUB_GUTTER),
 * 패딩 있는 목록 열(태블릿·데스크톱)은 열 끝까지(-mx-6 px-6).
 */
export function LandingBlocksSection({
  data,
  locale,
  gutter,
}: {
  data: LandingBlocks;
  locale: string;
  gutter: "mobile" | "hubMobile" | "padded";
}) {
  const t = useTranslations("search.landingBlock");
  const tSearch = useTranslations("search");
  const { formatWithCodeFromSource } = useCurrency();
  const price = (p: LandingPrice) => {
    const text = formatWithCodeFromSource(p.amount, p.currency);
    return p.varies ? t("priceFrom", { price: text }) : text;
  };
  const languages = locales.map((l) => LANGUAGE_NAMES[l] ?? l).join(" · ");
  const isCamping = data.kind === "camping";
  const inset = GUTTERS[gutter];
  const scroller = inset.scroller;

  const noun = data.noun?.group
    ? t(`noun.${data.noun.group}`)
    : (data.noun?.categoryName ?? t("noun.generic"));

  const summaryParts: string[] = [];
  if (data.summary) {
    const s = data.summary;
    summaryParts.push(t("summary.products", { n: s.products }));
    if (s.partners != null) summaryParts.push(t("summary.partners", { n: s.partners }));
    if (s.stations != null)
      summaryParts.push(t(isCamping ? "summary.sites" : "summary.stations", { n: s.stations }));
    if (s.models != null) summaryParts.push(t("summary.models", { n: s.models }));
    if (s.minPrice)
      summaryParts.push(
        t(s.minPriceUnit === "from" ? "priceFrom" : "summary.priceFrom", {
          price: price(s.minPrice),
        }),
      );
  }

  // 문장·항목 잇기 — ja·zh 는 。 뒤에 공백이 없고 항목은 、로 잇는다(ko·en 은 공백 · 쉼표).
  const cjk = locale === "ja" || locale.startsWith("zh");
  const sentenceSep = cjk ? "" : " ";
  const listSep = cjk ? "、" : ", ";
  const groupText = (groups: LandingOptionGroup[]) =>
    groups
      .map((g) =>
        t("faq.options.group", {
          name: g.name,
          choices: g.choices
            .map((c) =>
              t(g.perDay ? "faq.options.choicePerDay" : "faq.options.choice", {
                label: c.label,
                price: price(c.price),
              }),
            )
            .join(listSep),
        }),
      )
      .join(sentenceSep);

  const faqEntry = (item: LandingFaqItem): { q: string; a: string } | null => {
    switch (item.kind) {
      case "license":
        return {
          q: t("faq.license.q"),
          a: t("faq.license.a", {
            list: item.classes
              .map((key) => {
                const klass = getBikeClass(key);
                const license = bikeLicenseText(key, locale);
                if (!klass || !license) return null;
                const label = klass.label[locale as keyof typeof klass.label] ?? klass.label.en;
                return t("faq.license.item", { label, license });
              })
              .filter(Boolean)
              .join(" · "),
          }),
        };
      case "leadDays":
        return {
          q: t("faq.leadDays.q"),
          a:
            item.min === item.max
              ? t("faq.leadDays.same", { n: item.max })
              : t("faq.leadDays.varies", { n: item.max }),
        };
      case "hours24":
        return {
          q: t("faq.hours24.q"),
          a: t(isCamping ? "faq.hours24.campingA" : "faq.hours24.a", { n: item.stations }),
        };
      case "oneWay":
        return {
          q: t("faq.oneWay.q"),
          a: t("faq.oneWay.a", { unit: item.unit, price: price(item.price) }),
        };
      case "optionalOptions":
        return { q: t("faq.optional.q"), a: t("faq.optional.a", { groups: groupText(item.groups) }) };
      case "requiredOptions":
        return { q: t("faq.required.q"), a: t("faq.required.a", { groups: groupText(item.groups) }) };
      case "pickupMethods": {
        const labels = item.methods.map((m) => tSearch(`usageMethod.${m}`));
        return {
          q: t("faq.pickupMethods.q", { item: noun }),
          a:
            labels.length === 1
              ? t("faq.pickupMethods.one", { method: labels[0] })
              : t("faq.pickupMethods.many", { methods: labels.join(" · ") }),
        };
      }
      case "price":
        return {
          q: t("faq.price.q", { item: noun }),
          a:
            item.unit === "from"
              ? t("faq.price.aFrom", { price: price(item.from) })
              : item.longRentalDiscounts > 0
              ? `${t("faq.price.a", { price: price(item.from) })}${sentenceSep}${t(
                  "faq.price.long",
                  { n: item.longRentalDiscounts },
                )}`
              : t("faq.price.a", { price: price(item.from) }),
        };
      case "languages":
        return { q: t("faq.languages.q"), a: t("faq.languages.a", { languages }) };
    }
  };
  const faq = data.faq.map(faqEntry).filter((e): e is { q: string; a: string } => !!e);

  const values = data.values
    ? [
        data.values.open24h
          ? {
              title: t("values.open24hTitle"),
              body: t(isCamping ? "values.campingOpen24hBody" : "values.open24hBody", {
                n: data.values.stations,
              }),
            }
          : null,
        data.values.oneWay
          ? {
              title: t("values.oneWayTitle"),
              body: t("values.oneWayBody", {
                unit: data.values.oneWay.unit,
                price: price(data.values.oneWay.price),
              }),
            }
          : null,
        {
          title: t("values.languagesTitle", { n: locales.length }),
          body: t("values.languagesBody", { languages }),
        },
      ].filter((v): v is { title: string; body: string } => !!v)
    : [];

  // 카루셀 순서(지점 → 모델)로 앞 EAGER_IMAGES 장만 즉시
  const cardImage = (src: string | null, alt: string, order: number) => {
    const eager = order < EAGER_IMAGES;
    return (
      <div className="relative aspect-4/3 overflow-hidden rounded-xl bg-gray-100">
        {src ? (
          <OptimizedImage
            src={src}
            alt={alt}
            fill
            enablePlaceholder={false}
            loading={eager ? "eager" : "lazy"}
            sizes="(min-width: 640px) 200px, 156px"
            className="object-cover"
          />
        ) : null}
      </div>
    );
  };
  const cardClass = "block w-[156px] shrink-0 sm:w-[200px]";
  const sectionTitle = "text-[17px] font-semibold text-gray-900 sm:text-xl";

  return (
    <section
      aria-labelledby="landing-blocks-heading"
      className={cn(
        "relative isolate mt-10 overflow-hidden pt-8 pb-8",
        inset.section,
      )}
    >
      {/* 아주 옅은 주황 번짐 — 섹션 머리 뒤에만, 아래로 사라진다 */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[440px]"
        style={{
          background:
            "radial-gradient(55% 75% at 20% 0%, rgba(249,115,22,.13), rgba(249,115,22,0) 70%), radial-gradient(45% 60% at 85% 0%, rgba(251,146,60,.09), rgba(251,146,60,0) 70%)",
          WebkitMaskImage: "linear-gradient(#000 40%, transparent)",
          maskImage: "linear-gradient(#000 40%, transparent)",
          filter: "blur(28px)",
        }}
      />

      <h2
        id="landing-blocks-heading"
        className="text-[20px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-2xl"
      >
        {t("heading", { heading: data.heading })}
      </h2>
      {summaryParts.length > 0 ? (
        <p className="mt-1.5 text-sm text-gray-600" suppressHydrationWarning>
          {summaryParts.join(" · ")}
        </p>
      ) : null}

      {values.length > 0 ? (
        <ul className="mt-6 grid gap-4 sm:grid-cols-3 sm:gap-6">
          {values.map((v) => (
            <li key={v.title}>
              <p className="text-[15px] font-semibold text-gray-900">{v.title}</p>
              <p className="mt-0.5 text-[13px] leading-5 text-gray-600" suppressHydrationWarning>
                {v.body}
              </p>
            </li>
          ))}
        </ul>
      ) : null}

      {data.stationCards.length > 0 ? (
        <div className="mt-10">
          <h3 className={cn("mb-3", sectionTitle)}>
            {t(isCamping ? "stations.campingTitle" : "stations.title")}
          </h3>
          <ul className={cn("flex gap-3 overflow-x-auto pb-1 scrollbar-hide", scroller)}>
            {data.stationCards.map((card, i) => (
              <li key={card.href} className={cardClass}>
                <Link href={card.href} prefetch={false} className="block">
                  {cardImage(card.image, card.name, i)}
                  <p className="mt-2 line-clamp-1 text-[14px] font-medium text-gray-900">
                    {card.name}
                  </p>
                  {card.access ? (
                    <p className="mt-0.5 line-clamp-1 text-[12px] text-gray-500">{card.access}</p>
                  ) : null}
                  <p
                    className="mt-0.5 line-clamp-1 text-[13px] font-semibold text-gray-900"
                    suppressHydrationWarning
                  >
                    {[
                      card.open24h ? t("card.open24h") : null,
                      card.price
                        ? t(isCamping ? "card.from" : "card.perDay", {
                            price: formatWithCodeFromSource(card.price.amount, card.price.currency),
                          })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data.modelCards.length > 0 ? (
        <div className="mt-10">
          <h3 className={cn("mb-3", sectionTitle)}>
            {t(data.kind === "general" ? "models.generalTitle" : "models.title")}
          </h3>
          <ul className={cn("flex gap-3 overflow-x-auto pb-1 scrollbar-hide", scroller)}>
            {data.modelCards.map((card, i) => {
              const meta = [
                card.bikeType ? bikeTypeLabel(card.bikeType, locale) : null,
                card.transmission,
                card.stations != null ? t("card.stations", { n: card.stations }) : null,
                card.products != null ? t("card.products", { n: card.products }) : null,
              ].filter(Boolean);
              return (
                <li key={card.href} className={cardClass}>
                  <Link href={card.href} prefetch={false} className="block">
                    {cardImage(card.image, card.name, data.stationCards.length + i)}
                    <p className="mt-2 line-clamp-1 text-[14px] font-medium text-gray-900">
                      {card.name}
                    </p>
                    {meta.length > 0 ? (
                      <p className="mt-0.5 line-clamp-1 text-[12px] text-gray-500">
                        {meta.join(" · ")}
                      </p>
                    ) : null}
                    {card.price ? (
                      <p
                        className="mt-0.5 text-[13px] font-semibold text-gray-900"
                        suppressHydrationWarning
                      >
                        {t("card.perDay", {
                          price: formatWithCodeFromSource(card.price.amount, card.price.currency),
                        })}
                      </p>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {data.typeChips.length > 0 ? (
        <div className="mt-10">
          <h3 className={sectionTitle}>{t("types.title")}</h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {data.typeChips.map((chip) => (
              <li key={chip.type}>
                <Link
                  href={chip.href}
                  prefetch={false}
                  className="inline-flex items-baseline gap-1 rounded-full border border-gray-200 bg-white px-3.5 py-2 text-[14px] font-medium text-gray-900 hover:border-gray-300"
                >
                  {bikeTypeLabel(chip.type, locale)}
                  <span className="font-normal tabular-nums text-gray-500">{chip.count}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {faq.length > 0 ? (
        <div className="mt-10">
          <h3 className={sectionTitle}>{t("faq.title")}</h3>
          <div className="mt-2 border-t border-gray-200">
            {faq.map((entry) => (
              <details key={entry.q} className="border-b border-gray-200">
                <summary className="cursor-pointer list-none py-3.5 text-[15px] font-medium text-gray-900 [&::-webkit-details-marker]:hidden">
                  {entry.q}
                </summary>
                <p className="pb-4 text-[14px] leading-6 text-gray-600" suppressHydrationWarning>
                  {entry.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      ) : null}

      {data.pickup.length > 0 ? (
        <p className="mt-8 text-[14px] leading-6 text-gray-600">
          <span className="font-medium text-gray-900">{t("pickup.title")}</span>{" "}
          {data.pickup.map((place, i) => (
            <span key={`${place.name}-${i}`}>
              {i > 0 ? " · " : null}
              {place.href ? (
                <Link
                  href={place.href}
                  prefetch={false}
                  className="underline-offset-2 hover:text-gray-900 hover:underline"
                >
                  {place.name}
                </Link>
              ) : (
                place.name
              )}
            </span>
          ))}
        </p>
      ) : null}
    </section>
  );
}
